import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Link2, Plus, Edit2, Trash2, Search, Upload, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platforms = [
  { id: 'amazon_uae', name: 'Amazon UAE' },
  { id: 'amazon_ksa', name: 'Amazon KSA' },
  { id: 'amazon_au', name: 'Amazon Australia' },
  { id: 'noon_uae', name: 'Noon.com UAE' },
  { id: 'amazon_ind', name: 'Amazon India' },
  { id: 'myntra', name: 'Myntra' },
  { id: 'ajio', name: 'Ajio' },
  { id: 'flipkart', name: 'Flipkart' },
  { id: 'offline', name: 'Offline Store' },
];

export default function SKUMappingPage() {
  const [mappings, setMappings] = useState([]);
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    master_sku: '',
    platform: '',
    platform_sku: ''
  });
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTerm) {
      const filtered = mappings.filter((m) =>
        m.master_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.platform_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.platform.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMappings(filtered);
    } else {
      setFilteredMappings(mappings);
    }
  }, [searchTerm, mappings]);

  const fetchData = async () => {
    try {
      const [mappingsRes, productsRes] = await Promise.all([
        axios.get(`${API}/sku-mappings`),
        axios.get(`${API}/products`)
      ]);
      setMappings(mappingsRes.data);
      setFilteredMappings(mappingsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load SKU mappings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.master_sku || !formData.platform || !formData.platform_sku) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      await axios.post(`${API}/sku-mappings`, formData);
      toast({
        title: 'Success',
        description: 'SKU mapping created successfully',
      });
      setDialogOpen(false);
      setFormData({ master_sku: '', platform: '', platform_sku: '' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create mapping',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SKU mapping?')) return;
    
    try {
      await axios.delete(`${API}/sku-mappings/${id}`);
      toast({
        title: 'Success',
        description: 'SKU mapping deleted',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete mapping',
        variant: 'destructive',
      });
    }
  };

  const [importResult, setImportResult] = useState(null);

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      toast({
        title: 'Error',
        description: 'Please select a file to upload',
        variant: 'destructive',
      });
      return;
    }

    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkImportFile);

      const response = await axios.post(`${API}/sku-mappings/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      setImportResult(result);
      
      toast({
        title: 'Import Complete',
        description: `${result.created} new mappings created, ${result.updated} updated`,
      });

      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to import mappings',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const closeImportDialog = () => {
    setBulkImportDialogOpen(false);
    setBulkImportFile(null);
    setImportResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/sku-mappings/download-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sku_mapping_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download template', variant: 'destructive' });
    }
  };

  const getPlatformName = (platformId) => {
    return platforms.find(p => p.id === platformId)?.name || platformId;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">
              SKU Mapping
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredMappings.length} mappings {searchTerm && `(filtered from ${mappings.length})`}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setBulkImportDialogOpen(true)} variant="outline" data-testid="bulk-import-button">
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Bulk Import
            </Button>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-mapping-button">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Add Mapping
            </Button>
          </div>
        </div>

        <div className="bg-muted/30 border border-border rounded-sm p-4">
          <div className="flex items-start gap-3">
            <Link2 className="w-5 h-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium">Why SKU Mapping?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Different platforms may use different SKU codes for the same product. Map them to your master
                  SKU so sales from all platforms correctly update your central inventory.
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                {['amazon_ind', 'amazon_uae', 'amazon_ksa', 'amazon_au', 'noon_uae', 'myntra', 'ajio', 'flipkart'].map((platform) => {
                  const count = mappings.filter(m => m.platform === platform).length;
                  if (count === 0) return null;
                  return (
                    <div key={platform} className="bg-background rounded-sm p-2 border border-border">
                      <p className="text-xs text-muted-foreground">{getPlatformName(platform)}</p>
                      <p className="text-lg font-bold font-mono">{count}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              placeholder="Search mappings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full" data-testid="mappings-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Master SKU</th>
                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Platform</th>
                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Platform SKU</th>
                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Product Name</th>
                <th className="text-center p-3 text-xs uppercase tracking-widest font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.length > 0 ? (
                filteredMappings.map((mapping) => {
                  const product = products.find(p => p.sku === mapping.master_sku);
                  return (
                    <tr
                      key={mapping.id}
                      data-testid={`mapping-row-${mapping.id}`}
                      className="border-b border-border hover:bg-muted/30 transition-colors"
                    >
                      <td className="p-3 font-mono text-sm font-medium">{mapping.master_sku}</td>
                      <td className="p-3 text-sm">{getPlatformName(mapping.platform)}</td>
                      <td className="p-3 font-mono text-sm">{mapping.platform_sku}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {product ? product.name : '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleDelete(mapping.id)}
                            data-testid={`delete-${mapping.id}`}
                            className="p-1 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="p-12 text-center">
                    <p className="text-muted-foreground" data-testid="no-mappings-message">
                      {searchTerm
                        ? 'No mappings match your search'
                        : 'No SKU mappings yet. Create your first mapping to link platform SKUs to master inventory.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add Mapping Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle data-testid="dialog-title">Add SKU Mapping</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="mapping-form">
              <div>
                <label className="block text-sm font-medium mb-2">Master SKU *</label>
                <select
                  value={formData.master_sku}
                  onChange={(e) => setFormData({ ...formData, master_sku: e.target.value })}
                  data-testid="input-master-sku"
                  className="w-full p-2 border border-border rounded-sm bg-background"
                  required
                >
                  <option value="">Select master SKU...</option>
                  {products.map((product) => (
                    <option key={product.sku} value={product.sku}>
                      {product.sku} - {product.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  This is your warehouse/inventory SKU
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Platform *</label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  data-testid="input-platform"
                  className="w-full p-2 border border-border rounded-sm bg-background"
                  required
                >
                  <option value="">Select platform...</option>
                  {platforms.map((platform) => (
                    <option key={platform.id} value={platform.id}>
                      {platform.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Platform SKU *</label>
                <Input
                  data-testid="input-platform-sku"
                  value={formData.platform_sku}
                  onChange={(e) => setFormData({ ...formData, platform_sku: e.target.value })}
                  placeholder="Enter the SKU used on the platform"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The SKU code that appears in sales reports from this platform
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" data-testid="save-mapping-button">
                  Create Mapping
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setFormData({ master_sku: '', platform: '', platform_sku: '' });
                  }}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={bulkImportDialogOpen} onOpenChange={closeImportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle data-testid="bulk-import-dialog-title">Bulk Import SKU Mappings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!importResult ? (
                <>
                  <div className="bg-muted/30 border border-border rounded-sm p-4">
                    <p className="text-sm font-medium mb-2">File Format:</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload an Excel (.xlsx) or CSV file with the following columns:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• <strong>Master SKU</strong> (required)</li>
                      <li>• <strong>Amazon SKU INDIA</strong> (optional)</li>
                      <li>• <strong>Amazon SKU UAE</strong> (optional)</li>
                      <li>• <strong>Amazon SKU KSA</strong> (optional)</li>
                      <li>• <strong>Amazon SKU AU</strong> (optional)</li>
                      <li>• <strong>Noon SKU UAE</strong> (optional)</li>
                      <li>• <strong>Myntra SKU</strong> (optional)</li>
                      <li>• <strong>Ajio SKU</strong> (optional)</li>
                      <li>• <strong>Flipkart SKU</strong> (optional)</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-3">
                      <strong>Note:</strong> Only rows where platform SKU differs from master SKU will create mappings.
                      Multiple platform SKUs can map to the same master SKU.
                    </p>
                  </div>

                  <Button 
                    variant="outline" 
                    onClick={downloadTemplate} 
                    className="w-full"
                    data-testid="download-sku-mapping-template"
                  >
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Download Template
                  </Button>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select File</label>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setBulkImportFile(e.target.files[0])}
                      data-testid="bulk-import-file-input"
                    />
                  </div>

                  {bulkImportFile && (
                    <div className="text-sm text-muted-foreground">
                      Selected: {bulkImportFile.name}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={handleBulkImport}
                      disabled={!bulkImportFile || importing}
                      data-testid="import-button"
                      className="flex-1"
                    >
                      {importing ? (
                        <>
                          <span className="animate-pulse">Importing...</span>
                        </>
                      ) : (
                        'Import Mappings'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeImportDialog}
                      disabled={importing}
                      data-testid="cancel-import-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Import Results */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3">
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-2xl font-bold text-green-500">{importResult.created}</p>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3">
                        <p className="text-xs text-muted-foreground">Updated</p>
                        <p className="text-2xl font-bold text-blue-500">{importResult.updated}</p>
                      </div>
                    </div>

                    <div className="bg-muted/30 border border-border rounded-sm p-4 space-y-2">
                      <p className="text-sm font-medium">Import Summary</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Total rows in file: <strong>{importResult.total_rows_in_file}</strong></li>
                        <li>• Mappings created/updated: <strong>{importResult.total_processed}</strong></li>
                        {importResult.skipped_same_as_master > 0 && (
                          <li>• Skipped (same as master): <strong>{importResult.skipped_same_as_master}</strong></li>
                        )}
                      </ul>
                      {importResult.explanation && (
                        <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                          {importResult.explanation}
                        </p>
                      )}
                    </div>

                    {importResult.missing_products_count > 0 && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
                        <p className="text-sm font-medium text-yellow-600">Missing Products Warning</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {importResult.missing_products_warning}
                        </p>
                        {importResult.missing_products_sample?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Sample missing SKUs:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {importResult.missing_products_sample.slice(0, 5).map((sku, idx) => (
                                <span key={idx} className="text-xs bg-background px-2 py-0.5 rounded border border-border font-mono">
                                  {sku}
                                </span>
                              ))}
                              {importResult.missing_products_count > 5 && (
                                <span className="text-xs text-muted-foreground">
                                  +{importResult.missing_products_count - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      onClick={closeImportDialog}
                      className="w-full"
                      data-testid="close-import-result-button"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}